import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api as Api } from 'api/http';
import { CompanyContext } from 'contexts/CompanyContext';
import axios from 'axios';
import { proxyUrl } from 'utils/proxy';
import Resizer from 'react-image-file-resizer';
import useVoices from './useVoices';
import { AIContext } from 'contexts/AIContext';

/**
 * Custom hook for managing avatars
 * Provides CRUD operations and state management for company avatars
 */
const useAvatars = ({ autoFetch = true } = {}) => {
    const { company } = useContext(CompanyContext);
    const { aiModels } = useContext(AIContext);
    const qc = useQueryClient();

    // Voice management hook
    const { createVoice } = useVoices();
    
    // State management
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [operationLoading, setOperationLoading] = useState({});
    const [currentlyPlayingVoice, setCurrentlyPlayingVoice] = useState(null);
    const audioRef = useRef(null);
    
    // AI Avatar Videos state
    const [aiAvatarVideos, setAiAvatarVideos] = useState([]);
    const [loadingAIVideos, setLoadingAIVideos] = useState(false);
    const [aiVideosNextLink, setAiVideosNextLink] = useState(null);
    const [pendingAIVideos, setPendingAIVideos] = useState(new Set());
    const [pollingAIVideos, setPollingAIVideos] = useState(new Set());
    const pollingAITimeouts = useRef(new Map());
    
    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Set loading state for specific operations
     */
    const setOperationLoadingState = useCallback((operation, isLoading) => {
        setOperationLoading(prev => ({
            ...prev,
            [operation]: isLoading
        }));
    }, []);

    /**
     * Fetch avatars for the current company with pagination support
     */
    const fetchAvatars = useCallback(async (options = {}) => {
        if (!company?.id) {
            setAvatars([]);
            return;
        }

        const {
            page = 1,
            pageSize = 30,
            sort = '-created_at',
            filters = {},
            includes = ['company', 'image.thumbnail', 'scenes', 'scenes.thumbnail', 'voice', 'voice.audio'],
            append = false
        } = options;

        setLoading(!append);
        setError(null);

        try {
            // Build query parameters
            const queryParams = new URLSearchParams({
                'page[number]': page.toString(),
                'page[size]': pageSize.toString(),
                sort: sort,
                include: includes.join(',')
            });

            // Add filters
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(`filter[${key}]`, value.toString());
                }
            });

            const response = await Api.get(`/companies/${company.id}/avatars?${queryParams.toString()}`);
            
            if (append) {
                setAvatars(prev => [...prev, ...response.data]);
            } else {
                setAvatars(response.data);
            }

            return {
                data: response.data,
                meta: response.meta,
                links: response.links
            };
        } catch (err) {
            console.error('Error fetching avatars:', err);
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [company?.id]);

    /**
     * Fetch all avatars for the company (handles pagination automatically).
     * Routed through the React Query cache keyed by `[company.id, sort,
     * filters, includes]` so consumers mounting on a revisit get an instant
     * cache hit instead of re-running the whole paginated fetch.
     */
    const fetchAllAvatars = useCallback(async (options = {}) => {
        if (!company?.id) {
            setAvatars([]);
            return [];
        }

        const {
            sort = '-created_at',
            filters = {},
            includes = ['company', 'image.thumbnail', 'scenes', 'scenes.thumbnail', 'voice', 'voice.audio']
        } = options;

        const cacheKey = [
            'avatars',
            'legacyAll',
            String(company.id),
            sort,
            JSON.stringify(filters),
            includes.join(','),
        ];

        setLoading(true);
        setError(null);

        try {
            const allAvatars = await qc.fetchQuery({
                queryKey: cacheKey,
                staleTime: 60_000,
                queryFn: async () => {
                    let acc = [];
                    const filterQs = Object.entries(filters)
                        .map(
                            ([key, value]) =>
                                `&filter[${key}]=${encodeURIComponent(value)}`
                        )
                        .join('');
                    const initial = await Api.get(
                        `/companies/${company.id}/avatars?include=${includes.join(',')}&sort=${sort}&page[size]=30${filterQs}`
                    );
                    acc = [...initial.data];
                    const totalPages = Math.ceil(
                        initial.meta.total / initial.meta.per_page
                    );
                    if (totalPages > 1) {
                        const remainingCalls = [];
                        for (let page = 2; page <= totalPages; page++) {
                            remainingCalls.push(
                                Api.get(
                                    `/companies/${company.id}/avatars?include=${includes.join(',')}&sort=${sort}&page[number]=${page}&page[size]=30${filterQs}`
                                )
                            );
                        }
                        const responses = await Promise.all(remainingCalls);
                        responses.forEach((r) => {
                            acc = [...acc, ...r.data];
                        });
                    }
                    return acc;
                },
            });
            setAvatars(allAvatars);
            return allAvatars;
        } catch (err) {
            console.error('Error fetching all avatars:', err);
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [company?.id, qc]);

    /**
     * Fetch a specific avatar by ID
     */
    const fetchAvatar = useCallback(async (avatarId, includes = ['company', 'image.thumbnail', 'scenes', 'scenes.thumbnail', 'voice', 'voice.audio']) => {
        setOperationLoadingState(`fetch-${avatarId}`, true);
        setError(null);

        try {
            const response = await Api.get(`/avatars/${avatarId}?include=${includes.join(',')}`);
            return response.data;
        } catch (err) {
            console.error('Error fetching avatar:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`fetch-${avatarId}`, false);
        }
    }, [setOperationLoadingState]);

    /**
     * Helper function to resize image files
     */
    const resizeFile = useCallback((file) => {
        return new Promise((resolve) => {
            Resizer.imageFileResizer(
                file, 
                1024, 
                1024, 
                file.type.includes("png") ? "PNG" : "JPEG", 
                100, 
                0, 
                (uri) => {
                    resolve(uri);
                },
                "file"
            );
        });
    }, []);

    /**
     * Upload avatar image from a file
     * @param {File} file - The image file to upload
     * @param {string} avatarId - The avatar ID to attach the image to
     * @returns {Promise<void>}
     */
    const uploadAvatarImage = useCallback(async (file, avatarId) => {
        setOperationLoadingState(`upload-${avatarId}`, true);
        setError(null);

        try {
            // Resize the image first
            const resizedFile = await resizeFile(file);
            const type = resizedFile.type.includes("image") ? "image" : "video";

            // Get presigned URL
            const response = await Api.post(
                `/avatars/${avatarId}/image`, 
                {}, 
                { 
                    filename: resizedFile.name, 
                    type: type, 
                    visibility: "public" 
                }
            );

            const awsUri = response.data.presigned_url;

            // Upload to AWS
            await axios.put(awsUri, resizedFile, { 
                headers: { 
                    'Content-Type': resizedFile.type, 
                    'x-ms-blob-type': 'BlockBlob' 
                } 
            });

            // Refetch the avatar to get updated image URL
            const updatedAvatar = await fetchAvatar(avatarId);
            setAvatars(prev => prev.map(avatar => 
                avatar.id === avatarId ? updatedAvatar : avatar
            ));

            return updatedAvatar;
        } catch (err) {
            console.error('Error uploading avatar image:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`upload-${avatarId}`, false);
        }
    }, [resizeFile, fetchAvatar, setOperationLoadingState]);

    /**
     * Upload avatar image from a URL
     * @param {string} url - The image URL to upload
     * @param {string} avatarId - The avatar ID to attach the image to
     * @returns {Promise<void>}
     */
    const updateAvatarImageUrl = useCallback(async (url, avatarId) => {
        setOperationLoadingState(`upload-${avatarId}`, true);
        setError(null);

        try {
            // Fetch the image from URL
            const response = await fetch(url.startsWith('data:image') ? url : proxyUrl(url));
            const blob = await response.blob();

            // Determine image type
            const imageType = !blob?.type?.split('/')?.[1] 
                ? url?.includes("data:image/") 
                    ? url?.substring("data:image/"?.length, url.indexOf(";base64"))
                    : url?.split('.')?.pop()?.toLowerCase()
                : blob?.type?.split('/')?.[1];
            
            const name = `avatar.${imageType}`;
            const file = new File([blob], name, { type: `image/${imageType}` });
            const type = file.type.split("/")[0];

            // Get presigned URL
            const apiResponse = await Api.post(
                `/avatars/${avatarId}/image`, 
                {}, 
                { 
                    filename: name, 
                    type: type, 
                    visibility: "public" 
                }
            );

            const awsUri = apiResponse.data.presigned_url;

            // Upload to AWS
            await axios.put(awsUri, file, { 
                headers: { 
                    'Content-Type': file.type, 
                    'x-ms-blob-type': 'BlockBlob' 
                } 
            });

            // Refetch the avatar to get updated image URL
            const updatedAvatar = await fetchAvatar(avatarId);
            setAvatars(prev => prev.map(avatar => 
                avatar.id === avatarId ? updatedAvatar : avatar
            ));

            return updatedAvatar;
        } catch (err) {
            console.error('Error updating avatar image from URL:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`upload-${avatarId}`, false);
        }
    }, [fetchAvatar, setOperationLoadingState]);

    /**
     * Create a new avatar with optional image upload
     * @param {Object} avatarData - Avatar data
     * @param {string} avatarData.name - Avatar name (required)
     * @param {string} avatarData.description - Avatar description
     * @param {Object} avatarData.voiceData - Voice data object
     * @param {string} avatarData.voiceData.voice_id - ElevenLabs voice ID
     * @param {string} avatarData.voiceData.name - Voice name
     * @param {string} avatarData.voiceData.language_code - Voice language code
     * @param {string} avatarData.voiceData.accent - Voice accent
     * @param {string} avatarData.voiceData.preview_url - Voice preview URL
     * @param {number} avatarData.existingVoiceId - Existing company voice ID to use
     * @param {boolean} avatarData.default - Set as default avatar
     * @param {File} avatarData.imageFile - Optional image file to upload
     * @param {string} avatarData.imageUrl - Optional image URL to use
     */
    const createAvatar = useCallback(async (avatarData) => {
        if (!company?.id) {
            throw new Error('Company ID is required to create avatar');
        }

        setOperationLoadingState('create', true);
        setError(null);

        try {
            // Validate required fields
            if (!avatarData.name) {
                throw new Error('Avatar name is required');
            }

            let voiceId = avatarData.existingVoiceId;

            // Create or find voice if voiceData is provided
            if (avatarData.voiceData && !voiceId) {
                const { voice_id, name, language_code, accent, preview_url, platform_external_id } = avatarData.voiceData;
                
                // Create a new voice in the company using useVoices hook
                try {
                    const newVoice = await createVoice({
                        name: name,
                        language_code: language_code || company?.language_code || 'en',
                        accent: accent || null,
                        platform: 'elevenlabs',
                        platform_external_id: voice_id || platform_external_id,
                        audioUrl: preview_url // createVoice handles audio upload
                    });
                    voiceId = newVoice.id;
                } catch (voiceErr) {
                    console.error('Error creating voice:', voiceErr);
                    throw new Error(`Failed to create voice: ${voiceErr.message}`);
                }
            }

            // Create avatar with voice relationship
            const payload = {
                name: avatarData.name,
                description: avatarData.description || null,
                voice_id: voiceId,
                default: Boolean(avatarData.default)
            };

            const response = await Api.post(`/companies/${company.id}/avatars`, {}, payload);
            let newAvatar = response.data;

            // Handle image upload if provided
            if (avatarData.imageUrl) {
                // Use URL
                newAvatar = await updateAvatarImageUrl(avatarData.imageUrl, newAvatar.id);
            } else if (avatarData.imageFile) {
                // Upload file
                newAvatar = await uploadAvatarImage(avatarData.imageFile, newAvatar.id);
            }

            // Update local state - add new avatar to the top
            setAvatars(prev => [newAvatar, ...prev]);
            qc.invalidateQueries({ queryKey: ['avatars'] });

            return newAvatar;
        } catch (err) {
            console.error('Error creating avatar:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState('create', false);
        }
    }, [company?.id, setOperationLoadingState, uploadAvatarImage, updateAvatarImageUrl, fetchAvatar, createVoice, qc]);

    /**
     * Update an existing avatar with optimistic updates
     * @param {string|number} avatarId - The avatar ID
     * @param {Object} avatarData - Updated avatar data
     * @param {string} avatarData.name - Avatar name
     * @param {string} avatarData.description - Avatar description
     * @param {Object} avatarData.voiceData - Voice data object (to create new voice)
     * @param {number} avatarData.voiceId - Existing company voice ID to use
     * @param {boolean} avatarData.default - Set as default
     * @returns {Promise<Object>} Updated avatar
     */
    const updateAvatar = useCallback(async (avatarId, avatarData) => {
        setOperationLoadingState(`update-${avatarId}`, true);
        setError(null);

        // Optimistic update: immediately update local state
        const previousAvatars = avatars;
        const previousAvatar = avatars.find(avatar => avatar.id === avatarId) || {};
        if (avatarData.default !== undefined) {
            // If setting as default, unset all other defaults
            if (avatarData.default === true) {
                setAvatars(prev => prev.map(avatar => ({
                    ...avatar,
                    default: avatar.id === avatarId
                })));
            } else {
                // Just unset this one
                setAvatars(prev => prev.map(avatar => 
                    avatar.id === avatarId ? { ...avatar, default: false } : avatar
                ));
            }
        } else {
            // For non-default updates, just update the specific avatar
            setAvatars(prev => prev.map(avatar => 
                avatar.id === avatarId ? { ...avatar, ...avatarData } : avatar
            ));
        }

        try {
            const payload = {};
            if (avatarData.name !== undefined) payload.name = avatarData.name;
            if (avatarData.description !== undefined) payload.description = avatarData.description;
            if (avatarData.default !== undefined) payload.default = Boolean(avatarData.default);

            // Handle voice update if provided
            let voiceId = avatarData?.voiceId;
            if (avatarData?.voiceData || voiceId) {

                // Create or find voice if voiceData is provided
                if (avatarData.voiceData && !voiceId) {
                    const { voice_id, name, language_code, accent, preview_url } = avatarData.voiceData;
                    
                    // Create a new voice in the company using useVoices hook
                    try {
                        const newVoice = await createVoice({
                            name: name,
                            language_code: language_code || company?.language_code || 'en',
                            accent: accent || null,
                            platform: 'elevenlabs',
                            platform_external_id: voice_id,
                            audioUrl: preview_url // createVoice handles audio upload
                        });
                        voiceId = newVoice.id;
                    } catch (voiceErr) {
                        console.error('Error creating voice:', voiceErr);
                        throw new Error(`Failed to create voice: ${voiceErr.message}`);
                    }
                }
            }

            if (voiceId) payload.voice_id = voiceId;

            await Api.put(`/avatars/${avatarId}`, {}, payload);
            const updatedAvatar = await fetchAvatar(avatarId);

            // Update with actual backend data, maintaining the default logic
            setAvatars(prev => prev.map(avatar =>
                avatar.id === avatarId ? updatedAvatar : avatar
            ));
            qc.invalidateQueries({ queryKey: ['avatars'] });

            return updatedAvatar;
        } catch (err) {
            console.error('Error updating avatar:', err);
            // Rollback optimistic update on error
            setAvatars(previousAvatars);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`update-${avatarId}`, false);
        }
    }, [avatars, company?.id, setOperationLoadingState, fetchAvatar, createVoice, qc]);

    /**
     * Delete an avatar
     */
    const deleteAvatar = useCallback(async (avatarId) => {
        setOperationLoadingState(`delete-${avatarId}`, true);
        setError(null);

        try {
            await Api.delete(`/avatars/${avatarId}`);

            // Update local state
            setAvatars(prev => prev.filter(avatar => avatar.id !== avatarId));
            qc.invalidateQueries({ queryKey: ['avatars'] });

            return true;
        } catch (err) {
            console.error('Error deleting avatar:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`delete-${avatarId}`, false);
        }
    }, [setOperationLoadingState, qc]);

    /**
     * Set an avatar as the default for the company
     */
    const setDefaultAvatar = useCallback(async (avatarId) => {
        return await updateAvatar(avatarId, { default: true });
    }, [updateAvatar]);

    /**
     * Get the default avatar for the company
     */
    const defaultAvatar = useMemo(() => {
        return avatars.find(avatar => avatar.default) || null;
    }, [avatars]);

    /**
     * Get avatars filtered by criteria
     */
    const getFilteredAvatars = useCallback((filters = {}) => {
        return avatars.filter(avatar => {
            if (filters.voice && avatar.voice !== filters.voice) return false;
            if (filters.default !== undefined && avatar.default !== filters.default) return false;
            if (filters.name && !avatar.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
            return true;
        });
    }, [avatars]);

    /**
     * Check if an operation is currently loading
     */
    const isOperationLoading = useCallback((operation) => {
        return Boolean(operationLoading[operation]);
    }, [operationLoading]);

    /**
     * Upload a scene image for an avatar
     * @param {File} file - The image file to upload
     * @param {string} avatarId - The avatar ID to attach the scene to
     * @returns {Promise<Object>} Updated avatar with new scene
     */
    const uploadAvatarScene = useCallback(async (file, avatarId) => {
        setOperationLoadingState(`upload-scene-${avatarId}`, true);
        setError(null);

        try {
            const type = file.type.includes("image") ? "image" : "video";

            // Get presigned URL for scene upload
            const response = await Api.post(
                `/avatars/${avatarId}/scenes`, 
                {}, 
                { 
                    filename: file.name, 
                    type: type, 
                    visibility: "public" 
                }
            );

            const awsUri = response.data.presigned_url;

            // Upload to AWS
            await axios.put(awsUri, file, { 
                headers: { 
                    'Content-Type': file.type, 
                    'x-ms-blob-type': 'BlockBlob' 
                } 
            });

            // Refetch the avatar to get updated scenes
            const updatedAvatar = await fetchAvatar(avatarId);
            setAvatars(prev => prev.map(avatar => 
                avatar.id === avatarId ? updatedAvatar : avatar
            ));

            return updatedAvatar;
        } catch (err) {
            console.error('Error uploading avatar scene:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`upload-scene-${avatarId}`, false);
        }
    }, [fetchAvatar, setOperationLoadingState]);

    /**
     * Delete a scene from an avatar
     * @param {string} avatarId - The avatar ID
     * @param {string} sceneId - The scene ID to delete
     * @returns {Promise<Object>} Updated avatar without the deleted scene
     */
    const deleteAvatarScene = useCallback(async (avatarId, sceneId) => {
        setOperationLoadingState(`delete-scene-${sceneId}`, true);
        setError(null);

        try {
            await Api.delete(`/files/${sceneId}`);

            // Update local state - remove scene from avatar
            setAvatars(prev => prev.map(avatar => {
                if (avatar.id === avatarId) {
                    return {
                        ...avatar,
                        scenes: (avatar.scenes || []).filter(scene => scene.id !== sceneId)
                    };
                }
                return avatar;
            }));

            return true;
        } catch (err) {
            console.error('Error deleting avatar scene:', err);
            setError(err);
            throw err;
        } finally {
            setOperationLoadingState(`delete-scene-${sceneId}`, false);
        }
    }, [setOperationLoadingState]);

    /**
     * Play voice preview audio
     */
    const playVoicePreview = useCallback(async (avatar) => {
        try {
            // Stop any currently playing audio
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }

            // Create new audio instance
            audioRef.current = new Audio(avatar?.voice?.audio?.url);
            setCurrentlyPlayingVoice(avatar?.voice?.id);

            // Set up event listeners
            audioRef.current.addEventListener('ended', () => {
                setCurrentlyPlayingVoice(null);
            });

            audioRef.current.addEventListener('error', (e) => {
                console.error('Error playing voice preview:', e);
                setCurrentlyPlayingVoice(null);
            });

            // Play the audio
            await audioRef.current.play();
        } catch (err) {
            console.error('Error playing voice preview:', err);
            setCurrentlyPlayingVoice(null);
        }
    }, []);

    /**
     * Stop currently playing voice preview
     */
    const stopVoicePreview = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setCurrentlyPlayingVoice(null);
    }, []);

    /**
     * Toggle voice preview (play if stopped, stop if playing)
     */
    const toggleVoicePreview = useCallback((avatar) => {
        if (currentlyPlayingVoice === avatar?.voice?.id) {
            stopVoicePreview();
        } else {
            playVoicePreview(avatar);
        }
    }, [currentlyPlayingVoice, playVoicePreview, stopVoicePreview]);

    /**
     * Check if a voice preview is currently playing
     */
    const isVoicePlaying = useCallback((avatar) => {
        return currentlyPlayingVoice === avatar?.voice?.id;
    }, [currentlyPlayingVoice]);

    /**
     * Poll AI Avatar video status
     * @param {string} videoId - The video ID to poll
     */
    const pollAIVideoStatus = useCallback(async (videoId) => {
        let attempts = 0;
        const maxAttempts = 120; // 10 minutes max (120 * 5 seconds)
        const pollInterval = 10000; // 10 seconds
        let isPolling = true;
        
        const poll = async () => {
            if (!isPolling || attempts >= maxAttempts) return;
            
            attempts++;
            
            try {
                const pollResponse = await Api.get(`/aiResults/${videoId}`);
                const { status } = pollResponse.data;
                
                if (status === 'completed' || status === 'failed') {
                    isPolling = false;
                    
                    // Clean up timeout reference
                    pollingAITimeouts.current.delete(videoId);
                    
                    setPendingAIVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                    
                    setPollingAIVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                    
                    // Invalidate the cached first-page query so the refresh
                    // below actually hits the network and shows the just-
                    // completed video status instead of returning stale data.
                    qc.invalidateQueries({
                        queryKey: ['aiResults', 'avatarVideosFirstPage'],
                    });
                    fetchAIAvatarVideos(true);
                    return;
                }
                
                // Continue polling if still pending
                if (attempts < maxAttempts && isPolling) {
                    const timeoutId = setTimeout(poll, 5000);
                    pollingAITimeouts.current.set(videoId, timeoutId);
                }
                
            } catch (err) {
                isPolling = false;
                
                // Clean up timeout reference
                pollingAITimeouts.current.delete(videoId);
                
                // Remove from pending videos on error
                setPendingAIVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
                
                setPollingAIVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
            }
        };
        
        // Start the first poll after 10 seconds
        const timeoutId = setTimeout(poll, pollInterval);
        pollingAITimeouts.current.set(videoId, timeoutId);
        
        // Set up cleanup for max attempts
        setTimeout(() => {
            if (attempts >= maxAttempts && isPolling) {
                isPolling = false;
                
                // Clean up timeout reference
                pollingAITimeouts.current.delete(videoId);
                
                setPendingAIVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
                
                setPollingAIVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
            }
        }, maxAttempts * pollInterval);
    }, [qc]);

    /**
     * Fetch AI Avatar videos with pagination support
     * Filters by avatar video models (AI Avatar videos)
     * @param {boolean} replace - If true, replace existing videos, otherwise append
     * @returns {Promise<Object>} Response with data, meta, and links
     */
    // Refs for non-load-bearing deps so `fetchAIAvatarVideos` keeps a stable
    // identity across renders. Without this, every render of any
    // `useAvatars` parent (e.g. AIContext rebuilds `aiModels` as a fresh array
    // literal on every render) was bumping the function's identity, which
    // re-fired Dashboard's `useEffect([companyId, fetchAIAvatarVideos])` and
    // bypassed the RQ cache by churning before staleTime was respected.
    const aiModelsRef = useRef(aiModels);
    aiModelsRef.current = aiModels;
    const aiVideosNextLinkRef = useRef(aiVideosNextLink);
    aiVideosNextLinkRef.current = aiVideosNextLink;
    const pendingAIVideosRef = useRef(pendingAIVideos);
    pendingAIVideosRef.current = pendingAIVideos;
    const pollingAIVideosRef = useRef(pollingAIVideos);
    pollingAIVideosRef.current = pollingAIVideos;
    const pollAIVideoStatusRef = useRef(pollAIVideoStatus);
    pollAIVideoStatusRef.current = pollAIVideoStatus;

    const fetchAIAvatarVideos = useCallback(async (replace = true, videoType = 'video') => {
        if (!company?.id) {
            setAiAvatarVideos([]);
            return;
        }

        setLoadingAIVideos(true);
        setError(null);

        try {
            const modelFilter = aiModelsRef.current
                ?.filter(
                    (m) =>
                        m.isAvatarModel &&
                        (videoType === 'clip'
                            ? m.model !== 'creatomate_video'
                            : m.model === 'creatomate_video')
                )
                ?.map((m) => m.model)
                ?.join(',') || '';

            const isLoadMore = !replace && aiVideosNextLinkRef.current;
            const url = isLoadMore
                ? aiVideosNextLinkRef.current
                : `/aiResults?include=videos,videos.thumbnail,user,user.image&sort=-created_at&filter[company_id]=${company?.id}&filter[type]=video&filter[model]=${modelFilter}&page[size]=10`;

            // Cache only the first page (`replace=true`) — load-more uses an
            // opaque `links.next` URL that's already deduped by RQ if hit twice.
            let response;
            if (isLoadMore) {
                response = await Api.get(url);
            } else {
                response = await qc.fetchQuery({
                    queryKey: [
                        'aiResults',
                        'avatarVideosFirstPage',
                        String(company.id),
                        videoType,
                        modelFilter,
                    ],
                    staleTime: 60_000,
                    queryFn: () => Api.get(url),
                });
            }

            if (replace) {
                setAiAvatarVideos(response.data);
            } else {
                setAiAvatarVideos(prev => [...prev, ...response.data]);
            }

            setAiVideosNextLink(response.links?.next || null);

            // Check for pending videos and start polling for them
            const pending = response.data.filter(r => r.status === 'pending' || r.status === 'processing');
            if (pending.length > 0) {
                const newPendingIds = new Set([...pendingAIVideosRef.current, ...pending.map(p => p.id)]);
                setPendingAIVideos(newPendingIds);

                pending.forEach(video => {
                    if (!pollingAIVideosRef.current.has(video.id)) {
                        setPollingAIVideos(prev => new Set([...prev, video.id]));
                        pollAIVideoStatusRef.current(video.id);
                    }
                });
            }

            return {
                data: response.data,
                meta: response.meta,
                links: response.links
            };
        } catch (err) {
            console.error('Error fetching AI avatar videos:', err);
            setError(err);
            throw err;
        } finally {
            setLoadingAIVideos(false);
        }
    }, [company?.id, qc]);

    // Auto-fetch avatars when company changes
    useEffect(() => {
        if (!autoFetch) {
            return;
        }

        if (company?.id) {
            fetchAllAvatars().catch(console.error);
        } else {
            setAvatars([]);
        }
    }, [autoFetch, company?.id, fetchAllAvatars]);

    // Cleanup polling timeouts on unmount
    useEffect(() => {
        return () => {
            pollingAITimeouts.current.forEach(timeoutId => {
                clearTimeout(timeoutId);
            });
            pollingAITimeouts.current.clear();
        };
    }, []);

    return {
        // State
        avatars,
        loading,
        error,
        defaultAvatar,
        
        // Operations
        fetchAvatars,
        fetchAllAvatars,
        fetchAvatar,
        createAvatar,
        updateAvatar,
        deleteAvatar,
        setDefaultAvatar,
        uploadAvatarImage,
        updateAvatarImageUrl,
        
        // Scene Operations
        uploadAvatarScene,
        deleteAvatarScene,
        
        // Utilities
        getFilteredAvatars,
        clearError,
        isOperationLoading,

        // Voices
        playVoicePreview,
        stopVoicePreview,
        toggleVoicePreview,
        isVoicePlaying,
        
        // AI Avatar Videos
        aiAvatarVideos,
        loadingAIVideos,
        aiVideosNextLink,
        pendingAIVideos,
        fetchAIAvatarVideos,
        
        // Computed values
        avatarCount: avatars.length,
        hasAvatars: avatars.length > 0,
        hasDefaultAvatar: Boolean(defaultAvatar),
        hasAIAvatarVideos: aiAvatarVideos.length > 0,
        aiAvatarVideosCount: aiAvatarVideos.length,
    };
};

export default useAvatars;
